import { ChevronDown, MessageSquare, Shield, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/db';
import { captureSilentError } from '../services/posthog';
import { FeatureRequest, Feedback, FeedbackComment } from '../types';

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const currentUserUid = currentUser?.uid || '';
  
  const [activeTab, setActiveTab] = useState<'feedback' | 'features'>('feedback');
  
  // Feedback State
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [comments, setComments] = useState<FeedbackComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [feedbackPage, setFeedbackPage] = useState(0);
  const [hasMoreFeedback, setHasMoreFeedback] = useState(true);
  
  // Features State
  const [featureList, setFeatureList] = useState<FeatureRequest[]>([]);
  const [featurePage, setFeaturePage] = useState(0);
  const [hasMoreFeatures, setHasMoreFeatures] = useState(true);

  const PAGE_SIZE = 20;

  const loadFeedback = async (page: number, append = false) => {
    try {
        const data = await db.getFeedback(true, currentUserUid, PAGE_SIZE, page * PAGE_SIZE);
        if (data.length < PAGE_SIZE) setHasMoreFeedback(false);
        
        setFeedbackList(prev => append ? [...prev, ...data] : data);
    } catch (e) {
        captureSilentError("AdminPanel.loadFeedback", e);
    }
  };

  const loadFeatures = async (page: number, append = false) => {
    try {
        const data = await db.getFeatureRequests(currentUserUid, PAGE_SIZE, page * PAGE_SIZE);
        if (data.length < PAGE_SIZE) setHasMoreFeatures(false);

        setFeatureList(prev => append ? [...prev, ...data] : data);
    } catch (e) {
        captureSilentError("AdminPanel.loadFeatures", e);
    }
  };

  useEffect(() => {
    if (isOpen) {
        // Reset
        setFeedbackPage(0);
        setFeaturePage(0);
        setHasMoreFeedback(true);
        setHasMoreFeatures(true);
        loadFeedback(0);
        loadFeatures(0);
    }
  }, [isOpen]);

  const handleLoadMoreFeedback = () => {
      const nextPage = feedbackPage + 1;
      setFeedbackPage(nextPage);
      loadFeedback(nextPage, true);
  };

  const handleLoadMoreFeatures = () => {
      const nextPage = featurePage + 1;
      setFeaturePage(nextPage);
      loadFeatures(nextPage, true);
  };

  useEffect(() => {
    if (selectedFeedback) {
        db.getFeedbackComments(selectedFeedback.id).then(setComments);
    } else {
        setComments([]);
    }
  }, [selectedFeedback]);

  const handleSendComment = async () => {
    if (!selectedFeedback || !newComment.trim()) return;
    try {
        await db.addFeedbackComment(selectedFeedback.id, currentUserUid, newComment);
        setNewComment('');
        // Refresh comments
        const updatedComments = await db.getFeedbackComments(selectedFeedback.id);
        setComments(updatedComments);
    } catch (e: any) {
        setError(e.message || "Failed to send comment");
    }
  };

  const handleUpdateStatus = async (featureId: string, status: 'suggested' | 'in_progress' | 'complete') => {
      try {
          await db.updateFeatureStatus(featureId, status);
          setFeatureList(prev => prev.map(f => f.id === featureId ? { ...f, status } : f));
      } catch (e: any) {
          setError(e.message || "Failed to update status");
      }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-brand-dark/80 backdrop-blur-md animate-in fade-in">
      <div className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col h-[85vh]">
        
        {/* Header */}
        <div className="p-4 border-b border-brand-cream-dark flex justify-between items-center bg-brand-dark text-white">
           <div className="flex items-center gap-2">
               <Shield size={24} className="text-brand-red" />
               <h3 className="font-bold text-lg">Admin Command Center</h3>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full"><X size={20}/></button>
        </div>

        <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-48 bg-brand-cream-dark border-r border-brand-cream-dark p-4 space-y-2 shrink-0 z-10 relative">
                <button 
                    onClick={() => setActiveTab('feedback')}
                    className={`w-full text-left p-3 rounded-xl font-bold text-sm transition-all ${activeTab === 'feedback' ? 'bg-white shadow text-brand-dark' : 'text-brand-dark-light hover:text-brand-dark hover:bg-white/50'}`}
                >
                    Feedback
                </button>
                <button 
                    onClick={() => setActiveTab('features')}
                    className={`w-full text-left p-3 rounded-xl font-bold text-sm transition-all ${activeTab === 'features' ? 'bg-white shadow text-brand-dark' : 'text-brand-dark-light hover:text-brand-dark hover:bg-white/50'}`}
                >
                    Feature Requests
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 bg-white overflow-hidden flex">
                
                {/* FEEDBACK VIEW */}
                {activeTab === 'feedback' && (
                    <div className="flex-1 flex overflow-hidden">
                        {/* List */}
                        <div className="w-1/3 border-r border-brand-cream-dark overflow-y-auto">
                            {feedbackList.map(f => (
                                <div 
                                    key={f.id}
                                    onClick={() => setSelectedFeedback(f)}
                                    className={`p-4 border-b border-brand-cream-dark cursor-pointer hover:bg-brand-cream transition-colors ${selectedFeedback?.id === f.id ? 'bg-brand-cream border-l-4 border-l-brand-red' : ''}`}
                                >
                                    <div className="flex justify-between mb-1">
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded uppercase ${f.type === 'good' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {f.type}
                                        </span>
                                        <span className="text-xs text-brand-dark-light">{new Date(f.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    <p className="text-sm text-brand-dark line-clamp-2">{f.description}</p>
                                </div>
                            ))}
                            {hasMoreFeedback && (
                                <button 
                                    onClick={handleLoadMoreFeedback}
                                    className="w-full p-4 text-xs font-bold text-brand-dark-light hover:bg-brand-cream transition-colors flex justify-center items-center gap-1"
                                >
                                    Load More <ChevronDown size={14} />
                                </button>
                            )}
                        </div>

                        {/* Detail */}
                        <div className="flex-1 flex flex-col bg-brand-cream/30">
                            {selectedFeedback ? (
                                <>
                                    <div className="p-6 border-b border-brand-cream-dark bg-white">
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className={`text-sm font-bold px-3 py-1 rounded-full uppercase ${selectedFeedback.type === 'good' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {selectedFeedback.type === 'good' ? 'Positive Feedback' : 'Constructive Feedback'}
                                            </span>
                                            <span className="text-xs text-brand-dark-light ml-auto">ID: {selectedFeedback.id.slice(0,8)}</span>
                                        </div>
                                        <p className="text-lg text-brand-dark font-medium leading-relaxed">"{selectedFeedback.description}"</p>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                        {comments.map(c => (
                                            <div key={c.id} className={`flex gap-3 ${c.userId === currentUserUid ? 'flex-row-reverse' : ''}`}>
                                                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${c.userId === currentUserUid ? 'bg-brand-dark text-white rounded-tr-none' : 'bg-white border border-brand-cream-dark rounded-tl-none'}`}>
                                                    <p className="font-bold text-xs opacity-70 mb-1">{c.user?.displayName || 'Unknown'}</p>
                                                    <p>{c.content}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="p-4 bg-white border-t border-brand-cream-dark flex flex-col gap-2">
                                        {error && (
                                            <div className="bg-red-50 text-red-600 p-2 rounded-lg text-xs font-bold flex items-center gap-1">
                                                <span>⚠️</span> {error}
                                            </div>
                                        )}
                                        <div className="flex gap-2">
                                            <input 
                                                className="flex-1 p-3 border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-dark"
                                                placeholder="Write a reply..."
                                                value={newComment}
                                                onChange={e => { setNewComment(e.target.value); setError(null); }}
                                                onKeyDown={e => e.key === 'Enter' && handleSendComment()}
                                            />
                                            <button 
                                                onClick={handleSendComment}
                                                disabled={!newComment.trim()}
                                                className="p-3 bg-brand-dark text-white rounded-xl hover:bg-brand-dark/90 disabled:opacity-50"
                                            >
                                                <MessageSquare size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-brand-dark-light">
                                    Select feedback to view details
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* FEATURES VIEW */}
                {activeTab === 'features' && (
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        <h4 className="font-bold text-brand-dark mb-4">Manage Feature Requests</h4>
                        <div className="grid grid-cols-1 gap-4">
                            {featureList.map(feature => (
                                <div key={feature.id} className="bg-white p-4 rounded-xl border border-brand-cream-dark shadow-sm flex justify-between items-center">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <h5 className="font-bold text-brand-dark">{feature.title}</h5>
                                            <span className="text-xs bg-brand-cream-dark px-2 py-0.5 rounded-full text-brand-dark-light font-bold">
                                                {feature.voteCount} votes
                                            </span>
                                        </div>
                                        <p className="text-sm text-brand-dark-light">{feature.description}</p>
                                        <p className="text-xs text-brand-dark-light mt-1">By: {feature.user?.displayName}</p>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        <select 
                                            value={feature.status}
                                            onChange={(e) => handleUpdateStatus(feature.id, e.target.value as any)}
                                            className="p-2 border border-brand-cream-dark rounded-lg text-sm font-bold text-brand-dark outline-none focus:border-brand-dark"
                                        >
                                            <option value="suggested">Suggested</option>
                                            <option value="in_progress">In Progress</option>
                                            <option value="complete">Complete</option>
                                        </select>
                                    </div>
                                </div>
                            ))}
                            {hasMoreFeatures && (
                                <button 
                                    onClick={handleLoadMoreFeatures}
                                    className="w-full p-4 text-xs font-bold text-brand-dark-light hover:bg-brand-cream transition-colors flex justify-center items-center gap-1 rounded-xl border border-dashed border-brand-dark-light/30"
                                >
                                    Load More <ChevronDown size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                )}

            </div>
        </div>
      </div>
    </div>
  );
};
